import {Media, Message} from "@twilio/conversations";
import {Box} from "@mui/material";
import {shallowEqual, useSelector} from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {Key, useEffect, useRef, useState} from "react";

import {Colors, Container, Text, IconPerson} from "@bounce/shared/ui";
import {AppState} from "../store/definitions";
import {FilePreview} from "./FilePreview";
import {parseMessageBody} from "../utils/parseMessageBody";
import {formatTime, InputUtils} from "@bounce/shared/helpers";

// Simple message type for pre-engagement chat
export type ChatMessage = {
  id: string;
  author: "bot" | "user";
  body: string;
  timestamp: Date;
};

// Type guard to check if message is a Twilio Message
const isTwilioMessage = (message: Message | ChatMessage): message is Message => {
  return "index" in message && "dateCreated" in message;
};

export type MessageBubbleProps = {
  message: Message | ChatMessage;
  isLast?: boolean;
  isLastOfUserGroup: boolean;
  focusable?: boolean;
};

export const MessageBubble = ({
  message,
  isLast = false,
  isLastOfUserGroup,
  focusable = false,
}: MessageBubbleProps) => {
  const [read, setRead] = useState(false);
  const {conversationsClient, participants, users, fileAttachmentConfig} = useSelector(
    (state: AppState) => ({
      conversationsClient: state.chat.conversationsClient,
      participants: state.chat.participants,
      users: state.chat.users,
      fileAttachmentConfig: state.config.fileAttachment,
    }),
    shallowEqual
  );
  const messageRef = useRef<HTMLDivElement>(null);

  const isTwilio = isTwilioMessage(message);

  // Determine if message belongs to current user
  const belongsToCurrentUser = isTwilio
    ? message.author === conversationsClient?.user.identity
    : message.author === "user";

  // Get timestamp
  const timestamp = isTwilio ? message.dateCreated : message.timestamp;

  useEffect(() => {
    if (isTwilio && isLast && participants && belongsToCurrentUser) {
      const getOtherParticipants = participants.filter((p) => p.identity !== conversationsClient?.user.identity);
      setRead(
        Boolean(getOtherParticipants.length) &&
          getOtherParticipants.every((p) => p.lastReadMessageIndex === message.index)
      );
    } else {
      setRead(false);
    }
  }, [participants, isLast, belongsToCurrentUser, conversationsClient, message, isTwilio]);

  useEffect(() => {
    if (focusable) {
      messageRef.current?.focus();
    }
  }, [focusable]);

  const renderMedia = () => {
    if (!isTwilio) return null;

    if (fileAttachmentConfig?.enabled) {
      if (!message.attachedMedia) {
        return null;
      }

      return message.attachedMedia.map((media: Media, index: Key) => {
        const file = {
          name: media.filename,
          type: media.contentType,
          size: media.size,
        } as File;
        return <FilePreview key={index} file={file} isBubble={true} media={media} focusable={focusable} />;
      });
    }

    return <i>Media messages are not supported</i>;
  };

  // Get author display name
  const getAuthor = () => {
    if (!isTwilio) {
      return message.author === "user" ? "You" : "Bounce AI";
    }
    const messageAuthor = message.author?.split("_") || "";
    return (
      users?.find((u) => u.identity === message.author)?.friendlyName ||
      InputUtils.capitalizeString(messageAuthor[0]) ||
      undefined
    );
  };

  const author = getAuthor();

  return (
    <Box
      sx={{mb: "12px", gap: 0, alignItems: "stretch"}}
      ref={messageRef}
      data-message-bubble
    >
      <Container row sx={{gap: 0, alignItems: "flex-end"}}>
        {!belongsToCurrentUser && (
          <Container
            sx={{
              width: "24px",
              height: "24px",
              mr: "8px",
              borderRadius: "50%",
              overflow: "hidden",
              flex: "0 0 auto",
              backgroundColor: !isLastOfUserGroup ? "transparent" : Colors.primary._100,
              gap: 0,
              justifyContent: "center",
            }}
            data-testid="avatar-container"
          >
            {isLastOfUserGroup && <IconPerson style={{width: 20, height: 20, color: Colors.primary._700}} />}
          </Container>
        )}
        <Container
          sx={{
            py: "8px",
            px: "12px",
            backgroundColor: belongsToCurrentUser ? Colors.grey._100 : Colors.primary._100,
            borderRadius: "8px",
            ml: belongsToCurrentUser ? "auto" : 0,
            mr: belongsToCurrentUser ? 0 : "auto",
            maxWidth: "90%",
            gap: 0,
            alignItems: "flex-start",
          }}
        >
          <Container row fullWidth sx={{justifyContent: "space-between", gap: 0, mb: "4px"}}>
            <Text
              size={10}
              fontWeight={"bold"}
              color={Colors.grey._900}
              sx={{whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden"}}
            >
              {author}
            </Text>
            <Box component="span" sx={{position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)"}}>
              {belongsToCurrentUser ? "You sent at" : `${author} sent at`}
            </Box>
            {timestamp && (
              <Text
                size={10}
                fontWeight={"bold"}
                color={Colors.grey._900}
                sx={{ml: "12px"}}
              >
                {formatTime(timestamp)}
              </Text>
            )}
          </Container>
          <Text
            size={12}
            fontWeight={400}
            color={Colors.grey._900}
            sx={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}
          >
            {message.body ? parseMessageBody(message.body, belongsToCurrentUser) : null}
          </Text>
          {isTwilio && message.type === "media" ? renderMedia() : null}
        </Container>
      </Container>
      {read && (
        <Container row sx={{justifyContent: "flex-end", gap: "4px", mt: "4px"}}>
          <Text size={11} color={Colors.grey._900} sx={{textAlign: "right"}}>
            Read
          </Text>
          <CheckCircleIcon sx={{fontSize: "12px", color: Colors.grey._500}} />
        </Container>
      )}
    </Box>
  );
};
